// src/components/common/ProtectedRoute.js
import React from 'react';
import { useLocation, Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography
} from '@mui/material';
import { getCurrentUser } from '../../services/auth';
import useBillingVisibility from '../../hooks/useBillingVisibility';
import useEntitlements from '../../hooks/useEntitlements';

function ProtectedRoute({
  children,
  allowedAccountTypes = [],
  fallbackPath = '/dashboard',
  fallbackMessage,
  requireSubscription = false,
  requireCredits = false,
}) {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const user = getCurrentUser();
  const { shouldShowBilling } = useBillingVisibility();
  const entitlementRequired = requireSubscription || requireCredits;
  const {
    status: entitlementStatus,
    loading: entitlementLoading,
    error: entitlementError,
    refresh: refreshEntitlement,
  } = useEntitlements({ enabled: entitlementRequired });

  if (!token || !user) {
    return (
      <Navigate
        to="/login"
        state={{
          redirect: location.pathname + location.search,
          message: 'Please log in to access this page'
        }}
        replace
      />
    );
  }

  if (
    Array.isArray(allowedAccountTypes)
    && allowedAccountTypes.length > 0
    && !allowedAccountTypes.includes(user.accountType)
  ) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="warning">
          <AlertTitle>Access limited</AlertTitle>
          <Stack spacing={2}>
            <Typography variant="body2">
              {fallbackMessage || 'This section is not available for your account type.'}
            </Typography>
            <Button
              component={RouterLink}
              to={fallbackPath}
              variant="contained"
              color="primary"
            >
              Go back
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (entitlementRequired) {
    if (entitlementLoading) {
      return (
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Checking your billing entitlements...
          </Typography>
        </Container>
      );
    }

    if (entitlementError) {
      return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Unable to verify billing</AlertTitle>
            {entitlementError.response?.data?.message || entitlementError.message}
          </Alert>
          <Button variant="contained" onClick={refreshEntitlement}>
            Retry
          </Button>
        </Container>
      );
    }

    const wallet = entitlementStatus?.wallet || {};
    const requiredCredits = Number(wallet.requiredCredits || 0);
    const enforcementDisabled = entitlementStatus?.featureEnabled === false
      || entitlementStatus?.enforcementEnabled === false;
    const paymentRequired = entitlementStatus?.paymentRequired;

    if (enforcementDisabled) {
      return children;
    }
    const availableCredits = Number(wallet.balance || 0);
    const effectiveRequirement = requireCredits || requireSubscription
      ? Math.max(requiredCredits, 1)
      : requiredCredits;
    const needsCredits = requireSubscription || requireCredits || effectiveRequirement > 0;
    const hasCredits = effectiveRequirement > 0
      ? availableCredits >= effectiveRequirement
      : availableCredits > 0 || effectiveRequirement === 0;

    let allowed = needsCredits ? hasCredits : true;

    if (paymentRequired === false) {
      allowed = true;
    }

    if (!allowed) {
      const billingUnavailable = user.accountType !== 'university' && !shouldShowBilling;
      const billingPath = user.accountType === 'university'
        ? '/university/admin'
        : (billingUnavailable ? '/dashboard' : '/account/billing');
      const billingActionLabel = billingUnavailable ? 'Return to dashboard' : 'Manage billing';

      return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Alert severity="warning">
            <AlertTitle>Payment required</AlertTitle>
            <Stack spacing={2}>
              <Typography variant="body2">
                {needsCredits
                  ? `You need at least ${effectiveRequirement || 1} credits available to continue.`
                  : 'Wallet credits are required to continue.'}
              </Typography>
              <Button
                component={RouterLink}
                to={billingPath}
                variant="contained"
                color="primary"
              >
                {billingActionLabel}
              </Button>
            </Stack>
          </Alert>
        </Container>
      );
    }
  }

  return children;
}

export default ProtectedRoute;
